import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { OC, FONT } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import { isEmployerRole } from '../constants/index.js';
import { Button, Icon, Input, Card } from './ui.js';

// --- ApplyModal ---
function ApplyModal() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [coverLetter, setCoverLetter] = useState('');
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeName, setResumeName] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    if (!s.apply) return null;
    const op = s.findOpportunity(s.apply.id);
    const isInternal = s.apply.mode === 'internal' || op?.isInternalApply || op?.applicationType === 'Internal Apply';

    const resetForm = () => {
        setCoverLetter('');
        setResumeFile(null);
        setResumeName('');
        setError('');
        setBusy(false);
    };

    const close = () => {
        resetForm();
        s.closeApply();
    };

    const submitInternal = async () => {
        if (!resumeFile) {
            setError('Please attach your resume (PDF, DOC, or DOCX, max 5MB).');
            return;
        }
        setBusy(true);
        setError('');
        try {
            await s.submitInternalApplication(s.apply.id, { coverLetter, resumeFile });
            s.showToast('Application submitted', 'check');
            close();
        } catch (err) {
            setError(err.message || 'Could not submit application');
        } finally {
            setBusy(false);
        }
    };

    const onResumeChange = (event) => {
        const file = event.target.files?.[0] || null;
        setResumeFile(file);
        setResumeName(file?.name || '');
        setError('');
    };

    if (isInternal) {
        return React.createElement('div', {
            onClick: close,
            style: {
                position: 'fixed', inset: 0, zIndex: 90,
                background: 'rgba(0,0,0,.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 24, animation: 'ocFade .18s ease',
            }
        },
            React.createElement('div', {
                onClick: e => e.stopPropagation(),
                style: {
                    width: 520, maxWidth: '100%',
                    background: t.bg.overlay,
                    border: `1px solid ${t.border.base}`,
                    borderRadius: 20, padding: 30,
                    fontFamily: FONT.body,
                    boxShadow: t.shadow.lg,
                }
            },
                React.createElement('div', { style: { width: 52, height: 52, borderRadius: 14, background: OC.orangeMuted, color: OC.orange, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 } },
                    React.createElement(Icon, { name: 'briefcase', size: 24 })
                ),
                React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 21, color: t.text.primary, margin: '0 0 8px', letterSpacing: '-0.01em' } },
                    op ? `Apply to ${op.title}` : 'Submit application'
                ),
                React.createElement('p', { style: { fontSize: 14.5, color: t.text.secondary, margin: '0 0 18px', lineHeight: 1.55 } },
                    'This role accepts applications directly on Opportunity Circle. Upload your resume and optionally add a cover letter.'
                ),
                React.createElement('label', { style: { display: 'block', fontSize: 12.5, fontWeight: 700, color: t.text.secondary, marginBottom: 8 } }, 'Cover letter (optional)'),
                React.createElement('textarea', {
                    value: coverLetter,
                    onChange: e => setCoverLetter(e.target.value),
                    rows: 4,
                    placeholder: 'Tell the employer why you are a strong fit…',
                    style: {
                        width: '100%', boxSizing: 'border-box', resize: 'vertical',
                        border: `1px solid ${t.border.base}`, borderRadius: 12,
                        padding: '12px 14px', background: t.bg.page, color: t.text.primary,
                        fontFamily: FONT.body, fontSize: 14, lineHeight: 1.5, marginBottom: 16,
                    }
                }),
                React.createElement('label', { style: { display: 'block', fontSize: 12.5, fontWeight: 700, color: t.text.secondary, marginBottom: 8 } }, 'Resume'),
                React.createElement('div', {
                    style: {
                        border: `1px dashed ${t.border.strong}`,
                        borderRadius: 12,
                        padding: '16px 14px',
                        background: t.bg.subtle,
                        marginBottom: 10,
                    }
                },
                    React.createElement('input', {
                        type: 'file',
                        accept: '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        onChange: onResumeChange,
                        style: { width: '100%', fontFamily: FONT.body, fontSize: 13.5, color: t.text.primary },
                    }),
                    resumeName ? React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 8 } }, resumeName) : null
                ),
                React.createElement('div', { style: { fontSize: 12, color: t.text.secondary, marginBottom: 16 } }, 'PDF, DOC, or DOCX · Max 5MB'),
                error ? React.createElement('div', { style: { color: OC.urgent, fontSize: 13, marginBottom: 12 } }, error) : null,
                React.createElement(Button, {
                    kind: 'primary', size: 'lg', theme: s.theme, fullWidth: true,
                    onClick: submitInternal,
                    disabled: busy,
                    iconRight: React.createElement(Icon, { name: 'check', size: 16 }),
                }, busy ? 'Submitting…' : 'Submit application'),
                React.createElement(Button, { kind: 'ghost', size: 'md', theme: s.theme, fullWidth: true, onClick: close, style: { marginTop: 6 } }, 'Cancel')
            )
        );
    }

    return React.createElement('div', {
        onClick: s.closeApply,
        style: {
            position: 'fixed', inset: 0, zIndex: 90,
            background: 'rgba(0,0,0,.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 24, animation: 'ocFade .18s ease',
        }
    },
        React.createElement('div', {
            onClick: e => e.stopPropagation(),
            style: {
                width: 440, maxWidth: '100%',
                background: t.bg.overlay,
                border: `1px solid ${t.border.base}`,
                borderRadius: 20, padding: 30,
                fontFamily: FONT.body,
                boxShadow: t.shadow.lg,
            }
        },
            React.createElement('div', { style: { width: 52, height: 52, borderRadius: 14, background: OC.orangeMuted, color: OC.orange, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 } },
                React.createElement(Icon, { name: 'arrowUpRight', size: 24 })
            ),
            React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 21, color: t.text.primary, margin: '0 0 8px', letterSpacing: '-0.01em' } },
                op ? `You're heading to ${op.source}` : 'Open application'
            ),
            React.createElement('p', { style: { fontSize: 14.5, color: t.text.secondary, margin: '0 0 8px', lineHeight: 1.55 } },
                op ? React.createElement(React.Fragment, null,
                    'We opened the official application in a new tab for ',
                    React.createElement('strong', { style: { color: t.text.primary } }, op.title), '.'
                ) : 'Application link opened in a new tab.'
            ),
            React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 7, background: t.bg.subtle, borderRadius: 9, padding: '9px 12px', margin: '14px 0 22px', fontSize: 12.5, color: t.text.secondary } },
                React.createElement(Icon, { name: 'shieldCheck', size: 15, color: OC.success }),
                ' Reviewed by an Opportunity Circle volunteer'
            ),
            React.createElement(Button, { kind: 'primary', size: 'lg', theme: s.theme, fullWidth: true, onClick: s.confirmApplied, iconRight: React.createElement(Icon, { name: 'check', size: 16 }) }, 'I applied'),
            React.createElement(Button, { kind: 'ghost', size: 'md', theme: s.theme, fullWidth: true, onClick: s.closeApply, style: { marginTop: 6 } }, 'Cancel')
        )
    );
}

// --- ChangePasswordModal ---
function ChangePasswordModal({ onClose }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const [current, setCurrent] = useState('');
    const [next, setNext] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showCur, setShowCur] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [error, setError] = useState('');

    const valid = current.length >= 6 && next.length >= 6 && next === confirm && next !== current;

    const submit = () => {
        if (next !== confirm) { setError('New passwords do not match'); return; }
        if (next.length < 6) { setError('Password must be at least 6 characters'); return; }
        if (next === current) { setError('New password must be different from current'); return; }
        setError('');
        s.showToast('Password updated', 'check');
        onClose();
    };

    return React.createElement('div', {
        onClick: onClose,
        style: { position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }
    },
        React.createElement('div', {
            onClick: e => e.stopPropagation(),
            style: { width: 440, maxWidth: '100%', background: t.bg.overlay, border: `1px solid ${t.border.base}`, borderRadius: 20, padding: 30, fontFamily: FONT.body, boxShadow: t.shadow.lg }
        },
            React.createElement('div', { style: { width: 52, height: 52, borderRadius: 14, background: OC.orangeMuted, color: OC.orange, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 } },
                React.createElement(Icon, { name: 'lock', size: 22 })
            ),
            React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 21, color: t.text.primary, margin: '0 0 8px' } }, 'Change password'),
            React.createElement('p', { style: { fontSize: 14, color: t.text.secondary, margin: '0 0 20px' } }, "Choose a strong password you haven't used before."),
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 14 } },
                React.createElement(Input, {
                    theme: s.theme, label: 'Current password', value: current, onChange: setCurrent,
                    type: showCur ? 'text' : 'password', icon: 'lock',
                    rightSlot: React.createElement('button', { onClick: () => setShowCur(v => !v), style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary } }, React.createElement(Icon, { name: showCur ? 'eyeOff' : 'eye', size: 16 }))
                }),
                React.createElement(Input, {
                    theme: s.theme, label: 'New password', value: next, onChange: setNext,
                    type: showNext ? 'text' : 'password', icon: 'lock', hint: 'At least 6 characters',
                    rightSlot: React.createElement('button', { onClick: () => setShowNext(v => !v), style: { background: 'none', border: 'none', cursor: 'pointer', color: t.text.secondary } }, React.createElement(Icon, { name: showNext ? 'eyeOff' : 'eye', size: 16 }))
                }),
                React.createElement(Input, { theme: s.theme, label: 'Confirm new password', value: confirm, onChange: setConfirm, type: 'password', icon: 'lock' }),
                error ? React.createElement('div', { style: { fontSize: 13, color: OC.urgent, fontWeight: 600 } }, error) : null
            ),
            React.createElement('div', { style: { display: 'flex', gap: 10, marginTop: 22 } },
                React.createElement(Button, { kind: 'ghost', size: 'md', theme: s.theme, onClick: onClose, style: { flex: 1 } }, 'Cancel'),
                React.createElement(Button, { kind: 'primary', size: 'md', theme: s.theme, onClick: submit, style: { flex: 1, opacity: valid ? 1 : 0.5, cursor: valid ? 'pointer' : 'not-allowed', pointerEvents: valid ? 'auto' : 'none' } }, 'Update password')
            )
        )
    );
}

// --- SwitchAccountTypeModal ---
function SwitchAccountTypeModal({ onClose }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const [busy, setBusy] = useState(false);

    const confirmSwitch = async () => {
        if (busy) return;
        setBusy(true);
        try {
            if (!s.authed) {
                s.go('login');
                onClose();
                return;
            }
            if (!isEmployerRole(s.user.role)) {
                await s.registerAsEmployer();
            }
            s.setEmployerMode(true);
            s.showToast('Switched to Hiring Employer', 'briefcase');
            onClose();
            s.go('employers');
        } catch (err) {
            s.showToast(err.message || 'Could not switch account type', 'x');
        } finally {
            setBusy(false);
        }
    };
    return React.createElement('div', {
        onClick: onClose,
        style: { position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }
    },
        React.createElement('div', {
            onClick: e => e.stopPropagation(),
            style: { width: 420, maxWidth: '100%', background: t.bg.overlay, border: `1px solid ${t.border.base}`, borderRadius: 20, padding: 30, fontFamily: FONT.body, boxShadow: t.shadow.lg }
        },
            React.createElement('div', { style: { width: 52, height: 52, borderRadius: 14, background: OC.orangeMuted, color: OC.orange, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 } },
                React.createElement(Icon, { name: 'briefcase', size: 22 })
            ),
            React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 20, color: t.text.primary, margin: '0 0 8px' } }, 'Switch to Hiring Employer?'),
            React.createElement('p', { style: { fontSize: 14, color: t.text.secondary, margin: '0 0 22px', lineHeight: 1.55 } },
                "You'll get access to post roles and manage applicants. Your saved opportunities and applications stay on this account — switch back anytime from Settings."
            ),
            React.createElement('div', { style: { display: 'flex', gap: 10 } },
                React.createElement(Button, { kind: 'ghost', size: 'md', theme: s.theme, onClick: onClose, style: { flex: 1 } }, 'Cancel'),
                React.createElement(Button, { kind: 'primary', size: 'md', theme: s.theme, onClick: confirmSwitch, style: { flex: 1 } }, busy ? 'Switching…' : 'Confirm switch')
            )
        )
    );
}

// --- DeleteAccountModal ---
function DeleteAccountModal({ onClose }) {
    const s = useStore();
    const t = useTheme(s.theme);
    const [confirmText, setConfirmText] = useState('');
    const canDelete = confirmText.trim().toUpperCase() === 'DELETE';

    const deleteAccount = () => {
        s.showToast('Account deleted', 'x');
        onClose();
        s.setAuthed(false);
        s.go('home');
    };

    return React.createElement('div', {
        onClick: onClose,
        style: { position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }
    },
        React.createElement('div', {
            onClick: e => e.stopPropagation(),
            style: { width: 440, maxWidth: '100%', background: t.bg.overlay, border: `1px solid ${OC.urgent}55`, borderRadius: 20, padding: 30, fontFamily: FONT.body, boxShadow: t.shadow.lg }
        },
            React.createElement('div', { style: { width: 52, height: 52, borderRadius: 14, background: `${OC.urgent}1F`, color: OC.urgent, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 } },
                React.createElement(Icon, { name: 'x', size: 22 })
            ),
            React.createElement('h3', { style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 20, color: t.text.primary, margin: '0 0 8px' } }, 'Delete your account'),
            React.createElement('p', { style: { fontSize: 14, color: t.text.secondary, margin: '0 0 16px', lineHeight: 1.55 } },
                'This permanently deletes your profile, saved opportunities, and application history. This action cannot be undone.'
            ),
            React.createElement('div', { style: { fontSize: 13, color: t.text.primary, fontWeight: 600, marginBottom: 8 } }, 'Type DELETE to confirm'),
            React.createElement(Input, { theme: s.theme, value: confirmText, onChange: setConfirmText, placeholder: 'DELETE' }),
            React.createElement('div', { style: { display: 'flex', gap: 10, marginTop: 20 } },
                React.createElement(Button, { kind: 'ghost', size: 'md', theme: s.theme, onClick: onClose, style: { flex: 1 } }, 'Cancel'),
                React.createElement(Button, {
                    kind: 'destructive', size: 'md', onClick: deleteAccount,
                    style: { flex: 1, opacity: canDelete ? 1 : 0.5, cursor: canDelete ? 'pointer' : 'not-allowed', pointerEvents: canDelete ? 'auto' : 'none' }
                }, 'Delete my account')
            )
        )
    );
}


export { ApplyModal, ChangePasswordModal, SwitchAccountTypeModal, DeleteAccountModal };
